import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

import { formatDisplayDateTime as _formatDisplayDateTime } from './dateUtils'
export { formatDisplayDate, formatDisplayDateTime } from './dateUtils'

export function formatDate(date: string | Date | null | undefined) {
  if (!date) return 'Never'
  return _formatDisplayDateTime(date) || 'Never'
}

export function getConfidenceColor(confidence: number) {
  if (confidence >= 0.9) return 'confidence-high'
  if (confidence >= 0.7) return 'confidence-medium'
  return 'confidence-low'
}

export function getConfidenceLabel(confidence: number) {
  if (confidence >= 0.9) return 'High'
  if (confidence >= 0.7) return 'Medium'
  return 'Low'
}

/**
 * Normalizes formatted text values for better matching between LLM-extracted data and document text.
 * Removes common formatting elements like currency symbols, thousand separators, etc.
 */
export function normalizeFormattedText(text: string): string {
  if (!text) return text

  console.log('normalizeFormattedText input:', text)

  let result = text
    // Remove currency symbols (common ones)
    .replace(/[$€£¥₽₹₩₪₫₦₨₮₯₰₱₲₳₵₶₷₸₹₺₻₼₽₾₿]/g, '')
  
  // Remove thousand separators (spaces, commas, periods in number contexts)
  // Apply repeatedly until no more matches (to handle multiple separators like 89,803,409)
  let previousResult = ''
  while (previousResult !== result) {
    previousResult = result
    result = result.replace(/(\d)[.,\s](\d{3})/g, '$1$2')
  }

  // Remove ASCII control characters (including null bytes) that break matching
  result = result.replace(/[\u0000-\u001F\u007F]/g, '')

  // Normalize dash characters to a standard hyphen and remove separators in identifiers
  result = result.replace(/[–—−]/g, '-')
  let previousHyphenResult = ''
  while (previousHyphenResult !== result) {
    previousHyphenResult = result
    result = result.replace(/(\w)-(\w)/g, '$1$2')
  }
  
  // Remove extra whitespace
  result = result.replace(/\s+/g, ' ').trim()

  result = result.toLowerCase()

  console.log('normalizeFormattedText output:', result)
  return result
}

/**
 * Checks if two text values match after normalization
 */
export function normalizedTextMatch(text1: string, text2: string): boolean {
  const normalized1 = normalizeFormattedText(text1)
  const normalized2 = normalizeFormattedText(text2)
  const result = normalized1 === normalized2

  console.log('normalizedTextMatch:', {
    original1: text1,
    original2: text2,
    normalized1,
    normalized2,
    matches: result
  })

  return result
}

/**
 * Finds the best matching text block for a field value, considering formatting variations
 */
export function findBestTextMatch(fieldValue: string, textBlocks: Array<{ text: string; confidence: number }>): { text: string; confidence: number } | null {
  const normalizedFieldValue = normalizeFormattedText(fieldValue)

  // First try exact match after normalization
  for (const block of textBlocks) {
    if (normalizedTextMatch(block.text, fieldValue)) {
      return block
    }
  }

  // If no exact match, try partial matches (useful for multi-word fields)
  for (const block of textBlocks) {
    const normalizedBlockText = normalizeFormattedText(block.text)
    if (normalizedBlockText.includes(normalizedFieldValue) || normalizedFieldValue.includes(normalizedBlockText)) {
      return block
    }
  }

  // Return the block with highest confidence as fallback
  return textBlocks.length > 0 ? textBlocks[0] : null
}
